"use client";

import { useMemo } from 'react';
import { ArrowRight, Calendar, Clock, Sparkles } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { blogs } from '@/lib/blogs';
import { BlogHero } from '@/lib/content';

const BlogSection = () => {
    // 1. Performance: Memoize the blog list if it ever becomes dynamic or large
    const blogList = useMemo(() => blogs, []);

    return (
        <div className="min-h-screen bg-gray-50/50">
            {/* --- HERO BANNER --- */}
            <section
                className="relative min-h-[420px] sm:min-h-[500px] h-auto sm:h-[70vh] w-full flex items-center justify-center overflow-hidden bg-slate-900 px-4 sm:px-6 py-16 sm:py-0"
                aria-labelledby="blog-hero-title"
            >
                <div className="absolute inset-0 z-0">
                    <Image
                        src="/images/why2.jpg"
                        alt="" // Decorative image, alt should be empty
                        fill
                        className="object-cover opacity-40"
                        priority
                        fetchPriority="high"
                        sizes="100vw"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-transparent to-slate-900/80" />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto text-center">
                    <div className="mt-5 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/20 border border-brand/30 text-white text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] mb-6">
                        <Sparkles size={14} className="text-brand" aria-hidden="true" />
                        {BlogHero.subText}
                    </div>

                    <h1 id="blog-hero-title" className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-white mb-5 sm:mb-6 leading-[1.1] max-w-[95%] mx-auto">
                        {BlogHero.title}
                    </h1>

                    <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-300 max-w-full sm:max-w-[90%] md:max-w-2xl mx-auto leading-relaxed mb-8 sm:mb-10 px-2">
                        {BlogHero.description}
                    </p>

                    {/* Stats Bar with semantic grouping */}
                    <dl className="grid grid-cols-3 gap-3 sm:flex sm:flex-row items-start sm:items-center justify-center text-slate-400 text-xs sm:text-sm font-medium border-t border-white/10 pt-6 sm:pt-8 mt-4 w-full max-w-xl mx-auto">
                        {
                            BlogHero.stats.map((stat, index) => {
                                return (
                                    <div key={index} className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 text-center">
                                        <div className="hidden sm:block w-1.5 h-1.5 bg-brand rounded-full" aria-hidden="true" />
                                        <dt className="text-white font-bold text-lg sm:text-base leading-none">{stat.value}</dt>
                                        <dd className="uppercase tracking-wider text-[10px] sm:text-xs leading-none">{stat.description}</dd>

                                    </div>
                                )
                            })
                        }


                    </dl>
                </div>
            </section>

            {/* --- BLOG GRID --- */}
            <main className="max-w-7xl mx-auto px-4 py-20 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                    {blogList.sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map((blog) => (
                            <article
                                key={blog.id}
                                className="group relative flex flex-col bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-brand/30 transition-all duration-300 hover:shadow-[0_20px_50px_rgba(8,_112,_184,_0.1)]"
                            >
                                {/* 2. Accessibility & SEO: Single semantic Link wrapping the card */}
                                <Link
                                    href={`/insights/${blog.slug}`}
                                    className="absolute inset-0 z-20"
                                    aria-label={`Read article: ${blog.title}`}
                                />

                                <div className="relative h-56 w-full overflow-hidden">
                                    <div className="absolute top-4 left-4 z-10">
                                        <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-brand uppercase tracking-wider shadow-sm">
                                            {blog.category || 'Engineering'}
                                        </span>
                                    </div>
                                    <Image
                                        className="h-full w-full object-cover transform group-hover:scale-110 transition-transform duration-500 ease-in-out"
                                        src={blog.image}
                                        alt="" // If title is descriptive, alt can be empty to avoid redundancy
                                        fill
                                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                    />
                                </div>

                                <div className="flex flex-1 flex-col p-6">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-4 text-sm text-slate-400 mb-3">
                                            { /*<div className="flex items-center gap-2">
                                                <Calendar size={14} aria-hidden="true" />
                                                <time dateTime={blog.date}>{blog.date}</time>
                                            </div> */ }
                                            <div className="flex items-center gap-2">
                                                <Clock size={14} aria-hidden="true" />
                                                <span>{blog.readingTime}</span>
                                            </div>
                                        </div>

                                        <h2 className="text-2xl font-bold text-slate-900 group-hover:text-brand transition-colors duration-200 leading-tight">
                                            {blog.title}
                                        </h2>

                                        <p className="mt-4 text-slate-600 leading-relaxed line-clamp-3">
                                            {blog.description}
                                        </p>
                                    </div>

                                    <div className="mt-8 pt-6 border-t border-slate-100">
                                        <div className="flex items-center gap-2 text-sm font-bold text-slate-900 group-hover:text-brand transition-all">
                                            READ ARTICLE
                                            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))}
                </div>
            </main>
        </div>
    );
};

export default BlogSection;
